import { apiRequest } from '../../lib/httpClient'
import { deriveChartData, deriveKpiCards } from './utils'
import type {
  PracticeAgentReportData as ReportData,
  PracticeAgentGroupData as GroupData,
  PracticeAgentAIInsight as AIInsight,
  PracticeAgentWorkspaceInitData as WorkspaceInitData,
  PracticeAgentWorkspaceGroupInsightData,
  PracticeAgentVisualizationType as VisualizationType,
  PracticeAgentSendDeepDiveMessagePayload as SendDeepDiveMessagePayload,
  PracticeAgentSendDeepDiveMessageResponse as SendDeepDiveMessageResponse,
  PracticeAgentDataRow,
  PracticeAgentDrilldownData,
  PracticeAgentRecipient as PACommunicateRecipient,
  PracticeAgentCommunicateDraft as CommunicateDraft,
  PracticeAgentCommunicatePayload as CommunicatePayload,
  PracticeAgentCommunicationRecord as CommunicationRecord,
  PracticeAgentDraftPayload as DraftPayload,
  PracticeAgentRewritePayload as RewritePayload,
  PracticeAgentNote as Note,
  PracticeAgentAddNotePayload as AddNotePayload,
  PracticeAgentUpdateNotePayload as UpdateNotePayload,
  PracticeAgentActivity,
  PracticeAgentHistoryFilters,
  PracticeAgentHistoryListResponse,
  WorkspaceDimensions,
} from './types'

type Raw = Record<string, unknown>

function asString(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function asNumber(value: unknown, fallback = 0) {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function asStringArray(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((item) => asString(item)).filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

function mapPracticeAgentReportData(
  raw: Raw,
  fallbackReportBuilderId = '',
  fallbackFilters: Record<string, string> = {},
): ReportData {
  const visualizationTypeValue = asString(raw['visualizationType'] ?? raw['VisualizationType'])
  const visualizationType: VisualizationType =
    visualizationTypeValue === 'Bar' ||
    visualizationTypeValue === 'GroupedBar' ||
    visualizationTypeValue === 'Line' ||
    visualizationTypeValue === 'KPI' ||
    visualizationTypeValue === 'Table'
      ? visualizationTypeValue
      : 'Table'

  const appliedFiltersRaw = asObject(raw['appliedFilters'] ?? raw['AppliedFilters'])
  const appliedFilters = appliedFiltersRaw
    ? Object.entries(appliedFiltersRaw).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = asString(value)
        return acc
      }, {})
    : fallbackFilters

  const columns = asArray<unknown>(raw['columns'] ?? raw['Columns']).map(asString).filter(Boolean)
  const rows = asArray<Raw>(raw['rows'] ?? raw['Rows']) as unknown as PracticeAgentDataRow[]

  const rawChartData = asArray<Raw>(raw['chartData'] ?? raw['ChartData'])
  const rawKpiCards = asArray<Raw>(raw['kpiCards'] ?? raw['KpiCards'])

  const chartData = rawChartData.length > 0
    ? rawChartData.map((p: Raw) => ({
        label: asString(p['label'] ?? p['Label']),
        value: Number(p['value'] ?? p['Value']) || 0,
        series: asArray<Raw>(p['series'] ?? p['Series']).map((s: Raw) => ({
          name: asString(s['name'] ?? s['Name']),
          value: Number(s['value'] ?? s['Value']) || 0,
        })),
      }))
    : deriveChartData(columns, rows)

  const kpiCards = rawKpiCards.length > 0
    ? rawKpiCards.map((k: Raw) => ({
        label: asString(k['label'] ?? k['Label']),
        value: (k['value'] ?? k['Value']) as string | number,
        unit: asString(k['unit'] ?? k['Unit']) || undefined,
        trend: (k['trend'] ?? k['Trend']) as 'up' | 'down' | 'neutral' | undefined,
        trendValue: asString(k['trendValue'] ?? k['TrendValue']) || undefined,
      }))
    : deriveKpiCards(columns, rows)

  const totalRows = raw['totalRows'] ?? raw['TotalRows']
  const page = raw['page'] ?? raw['Page']
  const pageSize = raw['pageSize'] ?? raw['PageSize']
  const rawAxisMin = raw['axisMinValue'] ?? raw['AxisMinValue']
  const rawAxisMax = raw['axisMaxValue'] ?? raw['AxisMaxValue']
  const rawAxisMinByMeasure = raw['axisMinByMeasure'] ?? raw['AxisMinByMeasure']
  const rawAxisMaxByMeasure = raw['axisMaxByMeasure'] ?? raw['AxisMaxByMeasure']

  return {
    reportBuilderId: asString(raw['reportBuilderId'] ?? raw['ReportBuilderId']) || fallbackReportBuilderId,
    title: asString(raw['title'] ?? raw['Title']),
    visualizationType,
    columns,
    dimensions: asArray<unknown>(raw['dimensions'] ?? raw['Dimensions']).map(asString).filter(Boolean),
    rows,
    chartData,
    kpiCards,
    appliedFilters,
    retrievedAt: asString(raw['retrievedAt'] ?? raw['RetrievedAt']) || new Date().toISOString(),
    platformType: asNumber(raw['platformType'] ?? raw['PlatformType']),
    ...(totalRows != null ? { totalRows: Number(totalRows) } : {}),
    ...(page != null ? { page: Number(page) } : {}),
    ...(pageSize != null ? { pageSize: Number(pageSize) } : {}),
    ...(raw['groupByDimension'] ?? raw['GroupByDimension']
      ? { groupByDimension: asString(raw['groupByDimension'] ?? raw['GroupByDimension']) }
      : {}),
    ...(raw['sortColumn'] ?? raw['SortColumn']
      ? { sortColumn: asString(raw['sortColumn'] ?? raw['SortColumn']) }
      : {}),
    ...(raw['sortDirection'] ?? raw['SortDirection']
      ? { sortDirection: asString(raw['sortDirection'] ?? raw['SortDirection']) }
      : {}),
    ...(rawAxisMin != null ? { axisMinValue: Number(rawAxisMin) } : {}),
    ...(rawAxisMax != null ? { axisMaxValue: Number(rawAxisMax) } : {}),
    ...(rawAxisMinByMeasure != null && typeof rawAxisMinByMeasure === 'object'
      ? { axisMinByMeasure: Object.fromEntries(Object.entries(rawAxisMinByMeasure as Record<string, unknown>).map(([k, v]) => [k, Number(v)])) }
      : {}),
    ...(rawAxisMaxByMeasure != null && typeof rawAxisMaxByMeasure === 'object'
      ? { axisMaxByMeasure: Object.fromEntries(Object.entries(rawAxisMaxByMeasure as Record<string, unknown>).map(([k, v]) => [k, Number(v)])) }
      : {}),
  }
}

function mapPracticeAgentGroupData(raw: Raw, fallbackGroupId: string, fallbackFilters: Record<string, string>): GroupData {
  return {
    reportBuilderGroupId: asString(raw['reportBuilderGroupId'] ?? raw['ReportBuilderGroupId']) || fallbackGroupId,
    groupName: asString(raw['groupName'] ?? raw['GroupName']),
    reports: asArray<Raw>(raw['reports'] ?? raw['Reports']).map((item) =>
      mapPracticeAgentReportData(item, asString(item['reportBuilderId'] ?? item['ReportBuilderId']), fallbackFilters),
    ),
    linkedDimensions: asStringArray(raw['linkedDimensions'] ?? raw['LinkedDimensions']),
    retrievedAt: asString(raw['retrievedAt'] ?? raw['RetrievedAt']) || new Date().toISOString(),
  }
}

function mapInsight(raw: Raw): AIInsight {
  return {
    insightId: asString(raw['insightId'] ?? raw['InsightId']),
    reportBuilderId: asString(raw['reportBuilderId'] ?? raw['ReportBuilderId']) || undefined,
    reportBuilderGroupId: asString(raw['reportBuilderGroupId'] ?? raw['ReportBuilderGroupId']) || undefined,
    insight: asString(raw['insight'] ?? raw['Insight']),
    insertDate: (raw['insertDate'] ?? raw['InsertDate'] ?? null) as string | null,
    insertUserId: (raw['insertUserId'] ?? raw['InsertUserId'] ?? null) as string | null,
    updateDate: (raw['updateDate'] ?? raw['UpdateDate'] ?? null) as string | null,
    updateUserId: (raw['updateUserId'] ?? raw['UpdateUserId'] ?? null) as string | null,
  }
}

// GET /notificationbuilder/ui/reportBuilder/data
export async function fetchReportData(
  reportBuilderId: string,
  clientId: string,
  filters: Record<string, string> = {},
  reportBuilderGroupId?: string,
  page?: number,
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc',
  pageSize?: number,
  groupByDimension?: string,
  allRows?: boolean,
): Promise<ReportData> {
  if (!reportBuilderId || !clientId) {
    return {
      reportBuilderId,
      title: '',
      visualizationType: 'Table',
      columns: [],
      rows: [],
      chartData: [],
      kpiCards: [],
      appliedFilters: filters,
      retrievedAt: new Date().toISOString(),
      platformType: 0,
    }
  }

  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilder/data', {
    method: 'GET',
    query: {
      reportBuilderId,
      clientId,
      ...(reportBuilderGroupId ? { reportBuilderGroupId } : {}),
      ...(page != null ? { page: String(page) } : {}),
      ...(sortColumn ? { sortColumn } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      ...(pageSize ? { pageSize: String(pageSize) } : {}),
      ...(groupByDimension ? { groupByDimension } : {}),
      ...(allRows ? { allRows: 'true' } : {}),
      ...filters,
    },
  })

  const payloadObject = asObject(payload)
  const wrappedData = asObject(payloadObject?.['data'] ?? payloadObject?.['Data'])
  const source: Raw = wrappedData ?? asObject(payload) ?? {}

  return mapPracticeAgentReportData(source, reportBuilderId, filters)
}

// GET /notificationbuilder/ui/reportBuilder/groupData
export async function fetchGroupData(
  reportBuilderGroupId: string,
  clientId: string,
  linkedFilters: Record<string, string> = {},
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc',
): Promise<GroupData> {
  // Return default if missing IDs
  if (!reportBuilderGroupId || !clientId) {
    return {
      reportBuilderGroupId,
      groupName: '',
      reports: [],
      linkedDimensions: [],
      retrievedAt: new Date().toISOString(),
    }
  }

  // Build query params
  const query: Record<string, string> = {
    reportBuilderGroupId,
    clientId,
    // Spread linkedFilters here, safe because keys are dynamic
    ...linkedFilters,
    ...(sortColumn ? { sortColumn } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  }

  // Call the API
  const payload = await apiRequest<unknown>('notificationbuilder/ui/reportBuilder/groupData', {
    method: 'GET',
    query,
  })

  // Normalize response
  const payloadObject = asObject(payload)
  const wrappedData = asObject(payloadObject?.['data'] ?? payloadObject?.['Data'])
  const source: Raw = wrappedData ?? asObject(payload) ?? {}

  return mapPracticeAgentGroupData(source, reportBuilderGroupId, linkedFilters)
}

// GET /api/practiceagent/ui/workspace/insight
export async function fetchWorkspaceInsight(params: {
  clientId: string
  reportBuilderId: string
  filters?: Record<string, string>
}): Promise<AIInsight> {
  const { clientId, reportBuilderId, filters } = params
  const payload = await apiRequest<Raw>('practiceagent/ui/workspace/insight', {
    method: 'GET',
    query: { clientId, reportBuilderId, ...filters },
  })
  return mapInsight(asObject(payload) ?? {})
}

// GET /api/practiceagent/ui/workspace/groupInsight
export async function fetchWorkspaceGroupInsight(params: {
  clientId: string
  reportBuilderGroupId: string
  filters?: Record<string, string>
}): Promise<PracticeAgentWorkspaceGroupInsightData> {
  const { clientId, reportBuilderGroupId, filters } = params
  const payload = await apiRequest<Raw>('practiceagent/ui/workspace/groupInsight', {
    method: 'GET',
    query: { clientId, reportBuilderGroupId, ...filters },
  })
  const raw = asObject(payload) ?? {}
  const rawGroupInsight = asObject(raw['groupInsight'] ?? raw['GroupInsight'])
  return {
    groupInsight: rawGroupInsight ? mapInsight(rawGroupInsight) : undefined,
  }
}

// GET /api/practiceagent/ui/workspace/init
export async function fetchWorkspaceInit(params: {
  clientId: string
  reportBuilderId?: string
  reportBuilderGroupId?: string
  filters?: Record<string, string>
}): Promise<WorkspaceInitData> {
  const { clientId, reportBuilderId, reportBuilderGroupId, filters } = params
  const payload = await apiRequest<Raw>('practiceagent/ui/workspace/init', {
    method: 'GET',
    query: {
      clientId,
      ...(reportBuilderId ? { reportBuilderId } : {}),
      ...(reportBuilderGroupId ? { reportBuilderGroupId } : {}),
      ...filters,
    },
  })

  const raw = asObject(payload) ?? {}

  const rawReportData = asObject(raw['reportData'] ?? raw['ReportData'])
  const rawGroupData = asObject(raw['groupData'] ?? raw['GroupData'])

  return {
    reportData: rawReportData
      ? mapPracticeAgentReportData(rawReportData, reportBuilderId ?? '', filters ?? {})
      : undefined,
    groupData: rawGroupData
      ? mapPracticeAgentGroupData(rawGroupData, reportBuilderGroupId ?? '', filters ?? {})
      : undefined,
    insight: (raw['insight'] ?? raw['Insight']) as AIInsight | undefined,
    groupInsight: (raw['groupInsight'] ?? raw['GroupInsight']) as AIInsight | undefined,
    reportInsights: (raw['reportInsights'] ?? raw['ReportInsights']) as Record<string, AIInsight> | undefined,
  }
}

// GET /api/practiceagent/ui/workspace/find
export async function fetchWorkspaceByTitle(
  clientId: string,
  title: string
): Promise<{ type: string; id: string } | null> {
  if (!clientId || !title) return null
  const payload = await apiRequest<{ type: string; id: string } | null>(
    'practiceagent/ui/workspace/find',
    { method: 'GET', query: { clientId, title } }
  )
  return payload ?? null
}

// ── Deep Dive ─────────────────────────────────────────────────────────────────

// GET /api/practiceagent/ui/drilldown/data
export async function fetchDrilldownData(
  reportBuilderId: string,
  field: string,
  value: string,
  dimension: string,
  page = 1,
  reportBuilderGroupId?: string,
  allRows?: boolean,
): Promise<PracticeAgentDrilldownData> {
  const query: Record<string, string> = { reportBuilderId, field, value, dimension }
  if (page > 1) query['page'] = String(page)
  if (reportBuilderGroupId) query['reportBuilderGroupId'] = reportBuilderGroupId
  if (allRows) query['allRows'] = 'true'
  const payload = await apiRequest<unknown>('practiceagent/ui/drilldown/data', { query })
  const source = asObject(payload) ?? {}
  const columns = asArray<unknown>(source['columns'] ?? source['Columns']).map(asString).filter(Boolean)
  const rows = asArray<Raw>(source['rows'] ?? source['Rows']) as unknown as PracticeAgentDataRow[]
  const insight = asString(source['insight'] ?? source['Insight'])

  const rawChartData = asArray<Raw>(source['chartData'] ?? source['ChartData'])
  const rawKpiCards = asArray<Raw>(source['kpiCards'] ?? source['KpiCards'])

  const chartData = rawChartData.length > 0
    ? rawChartData.map((p: Raw) => ({
        label: asString(p['label'] ?? p['Label']),
        value: Number(p['value'] ?? p['Value']) || 0,
        series: asArray<Raw>(p['series'] ?? p['Series']).map((s: Raw) => ({
          name: asString(s['name'] ?? s['Name']),
          value: Number(s['value'] ?? s['Value']) || 0,
        })),
      }))
    : deriveChartData(columns, rows)

  const kpiCards = rawKpiCards.length > 0
    ? rawKpiCards.map((k: Raw) => ({
        label: asString(k['label'] ?? k['Label']),
        value: (k['value'] ?? k['Value']) as string | number,
        unit: asString(k['unit'] ?? k['Unit']) || undefined,
        trend: (k['trend'] ?? k['Trend']) as 'up' | 'down' | 'neutral' | undefined,
        trendValue: asString(k['trendValue'] ?? k['TrendValue']) || undefined,
      }))
    : deriveKpiCards(columns, rows)

  const totalRows = source['totalRows'] ?? source['TotalRows']
  const pageNum = source['page'] ?? source['Page']
  const pageSize = source['pageSize'] ?? source['PageSize']

  return {
    columns,
    rows,
    insight,
    chartData,
    kpiCards,
    ...(totalRows != null ? { totalRows: Number(totalRows) } : {}),
    ...(pageNum != null ? { page: Number(pageNum) } : {}),
    ...(pageSize != null ? { pageSize: Number(pageSize) } : {}),
  }
}


// GET /api/practiceagent/ui/drilldown/dimensions
export async function fetchDrilldownDimensions(
  clientId: string,
  platformType: number,
): Promise<string[]> {
  const payload = await apiRequest<unknown>('practiceagent/ui/drilldown/dimensions', {
    query: { clientId, platformType },
  })
  return asArray<unknown>(payload).map(asString).filter(Boolean)
}

// GET /api/practiceagent/ui/drilldown/workspaceDimensions
export async function fetchWorkspaceDimensions(
  clientId: string,
  reportBuilderId?: string,
  reportBuilderGroupId?: string,
): Promise<WorkspaceDimensions> {
  return apiRequest('practiceagent/ui/drilldown/workspaceDimensions', {
    query: { clientId, reportBuilderId, reportBuilderGroupId },
  })
}

// POST /api/practiceagent/ui/deepDive/message
// Stateless: caller passes full conversation history; backend uses it for context.
export async function sendDeepDiveMessage(payload: SendDeepDiveMessagePayload): Promise<SendDeepDiveMessageResponse> {
  return apiRequest('practiceagent/ui/deepDive/message', { method: 'POST', body: payload })
}

// ── Communicate ───────────────────────────────────────────────────────────────

// GET /api/practiceagent/ui/communicate/recipients
export async function fetchPARecipients(clientId: string): Promise<PACommunicateRecipient[]> {
  return apiRequest('practiceagent/ui/communicate/recipients', { query: { clientId } })
}

// POST /api/practiceagent/ui/communicate/draft
export async function fetchCommunicateDraft(payload: DraftPayload): Promise<CommunicateDraft> {
  return apiRequest('practiceagent/ui/communicate/draft', { method: 'POST', body: payload })
}

// POST /api/practiceagent/ui/communicate/send
export async function sendCommunication(payload: CommunicatePayload): Promise<CommunicationRecord> {
  return apiRequest('practiceagent/ui/communicate/send', { method: 'POST', body: payload })
}

// POST /api/practiceagent/ui/communicate/rewrite
export async function rewriteMessage(payload: RewritePayload): Promise<{ body: string }> {
  return apiRequest('practiceagent/ui/communicate/rewrite', { method: 'POST', body: payload })
}

// ── History ───────────────────────────────────────────────────────────────────

// GET /api/practiceagent/ui/history
export async function fetchHistory(clientId: string, filters: PracticeAgentHistoryFilters = {}): Promise<PracticeAgentHistoryListResponse> {
  const { type, fromDate, toDate, reportTitle, reportBuilderId, reportBuilderGroupId, insertUserId, page, pageSize } = filters
  return apiRequest('practiceagent/ui/history', {
    query: {
      clientId,
      type: type === 'all' ? undefined : type,
      fromDate,
      toDate,
      reportTitle,
      reportBuilderId,
      reportBuilderGroupId,
      insertUserId,
      page,
      pageSize,
    },
  })
}

// GET /api/practiceagent/ui/history/:activityId
export async function fetchActivityDetail(activityId: string): Promise<PracticeAgentActivity> {
  return apiRequest(`practiceagent/ui/history/${activityId}`)
}

// ── Notes ─────────────────────────────────────────────────────────────────────

// GET /api/practiceagent/ui/notes
export async function fetchNotes(params: {
  reportBuilderId?: string | null
  reportBuilderGroupId?: string | null
  clientId: string
}): Promise<Note[]> {
  return apiRequest('practiceagent/ui/notes', {
    query: {
      clientId: params.clientId,
      reportBuilderId: params.reportBuilderId ?? undefined,
      reportBuilderGroupId: params.reportBuilderGroupId ?? undefined,
    },
  })
}

// POST /api/practiceagent/ui/notes
export async function addNote(payload: AddNotePayload): Promise<Note> {
  return apiRequest('practiceagent/ui/notes', { method: 'POST', body: payload })
}

// PUT /api/practiceagent/ui/notes/:noteId
export async function updateNote(payload: UpdateNotePayload): Promise<Note> {
  return apiRequest(`practiceagent/ui/notes/${payload.noteId}`, { method: 'PUT', body: { note: payload.note } })
}

// DELETE /api/practiceagent/ui/notes/:noteId
export async function deleteNote(noteId: string): Promise<boolean> {
  return apiRequest(`practiceagent/ui/notes/${noteId}`, { method: 'DELETE' })
}
